import React from 'react';

import { Alert, Box, CircularProgress, FormHelperText, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import PaymentMethodCard from '../../../shared/components/PaymentMethodCard/PaymentMethodCard';
import { headerSx, rootSx, titleSx } from '../styles';
import { gridSx } from './styles';
import { PaymentMethodType } from '../../service/models';
import { useAvailableWithdrawInstruments, WithdrawMethodOption } from './useAvailableWithdrawInstruments';

interface PaymentMethodStepProps {
  selectedMethod: PaymentMethodType | null;
  onSelect: (method: PaymentMethodType, option: WithdrawMethodOption) => void;
  error?: string;
}

const PaymentMethodStep: React.FC<PaymentMethodStepProps> = ({ selectedMethod, onSelect, error }) => {
  const { t } = useTranslation('area');
  const { methods, loading, error: fetchError } = useAvailableWithdrawInstruments();

  const getStyle = (methodKey: string) => ({
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: selectedMethod === methodKey ? '2px solid #1976d2' : '2px solid transparent',
    borderRadius: '12px',
    opacity: selectedMethod && selectedMethod !== methodKey ? 0.6 : 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      flexGrow: 1,
      height: '100%',
    },
  });

  return (
    <Stack sx={rootSx}>
      <Stack sx={headerSx}>
        <Typography sx={titleSx}>{t('MODAL_WITHDRAW.CONTENT.STEP_ONE.SELECT_PAYMENT_METHOD')}</Typography>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Stack>

      <Box sx={gridSx}>
        {loading && <CircularProgress size={24} />}
        {fetchError && <Alert severity="error">{fetchError}</Alert>}
        {!loading && !fetchError && methods.length === 0 && (
          <Alert severity="info" sx={{ gridColumn: '1 / -1' }}>{t('MODAL_WITHDRAW.CONTENT.STEP_ONE.NO_PAYMENT_METHODS_AVAILABLE')}</Alert>
        )}
        {methods.map((option) => (
          <Box key={option.key} onClick={() => onSelect(option.key, option)} sx={getStyle(option.key)}>
            <PaymentMethodCard title={option.title} icon={option.icon} />
          </Box>
        ))}
      </Box>
    </Stack>
  );
};

export default PaymentMethodStep;
